import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Cpu,
  Droplets,
  ToggleRight,
  Sun,
  FolderKanban,
  Users,
  UserCog,
  Code2,
  FileSpreadsheet,
  Wifi,
  Package,
  ImageIcon,
  Sun as SunIcon,
  Moon,
  LogOut,
  ChevronDown,
  Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useProject } from "@/context/ProjectContext";
import { useBranding } from "@/context/BrandingContext";
import { LogoCluster } from "@/components/LogoBadges";
import { AppIcon } from "@/components/AppIcon";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DASHBOARD, NAV } from "@/constants/testIds";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: NAV.dashboard, end: true },
  { to: "/control", label: "Solenoid Control", icon: ToggleRight, testid: NAV.control },
  { to: "/sensors", label: "Sensor Manajemen", icon: Cpu, testid: NAV.sensors },
  { to: "/water", label: "Manajemen Air", icon: Droplets, testid: NAV.water },
  { to: "/energy", label: "Energi Surya", icon: Sun, testid: NAV.energy },
];

const adminItems = [
  { to: "/projects", label: "Proyek", icon: FolderKanban, testid: NAV.projects, masterOnly: true },
  { to: "/users", label: "Pengguna", icon: Users, testid: NAV.users, masterOnly: true },
  { to: "/account", label: "Account Settings", icon: UserCog, testid: "nav-account", masterOnly: true },
  { to: "/branding", label: "Branding", icon: ImageIcon, testid: "nav-branding", masterOnly: true },
  { to: "/wizard", label: "ESP32 Wizard", icon: Wifi, testid: "nav-wizard" },
  { to: "/firmware", label: "OTA Firmware", icon: Package, testid: "nav-firmware" },
  { to: "/arduino", label: "Kode Arduino", icon: Code2, testid: NAV.arduino },
  { to: "/sheets", label: "Google Sheets", icon: FileSpreadsheet, testid: "nav-sheets" },
];

function SidebarContent({ onNavigate }) {
  const { user } = useAuth();
  const { app } = useBranding();
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <AppIcon url={app.icon_url} size={36} />
          <div>
            <div className="font-display font-bold text-lg leading-none" data-testid="header-app-name">{app.app_name}</div>
            <div className="text-[10px] text-muted-foreground tracking-[0.15em] uppercase mt-1">
              IoT Dashboard
            </div>
          </div>
        </div>
        <div className="mt-4">
          <LogoCluster />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        <div>
          <div className="overline text-muted-foreground px-3 mb-2">Monitoring</div>
          <ul className="space-y-1">
            {navItems.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.end}
                  data-testid={it.testid}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                      isActive
                        ? "bg-secondary text-foreground font-medium"
                        : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                    }`
                  }
                >
                  <it.icon className="w-4 h-4" />
                  {it.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {(user?.role === "admin" || user?.role === "project_admin") && (
          <div>
            <div className="overline text-muted-foreground px-3 mb-2">{user.role === "admin" ? "Master Admin" : "Admin Anggota"}</div>
            <ul className="space-y-1">
              {adminItems.filter((it) => !it.masterOnly || user.role === "admin").map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    data-testid={it.testid}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                        isActive
                          ? "bg-secondary text-foreground font-medium"
                          : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                      }`
                    }
                  >
                    <it.icon className="w-4 h-4" />
                    {it.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { projects, currentId, selectProject } = useProject();
  const { app } = useBranding();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-background bg-grain" data-testid={DASHBOARD.root}>
      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 border-r border-border min-h-screen sticky top-0 bg-background flex-col">
          <SidebarContent />
        </aside>

        <main className="flex-1 min-w-0">
          {/* Header */}
          <header className="sticky top-0 z-30 backdrop-blur bg-background/85 border-b border-border">
            <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3">
              <div className="flex items-center gap-3">
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                      <Menu className="w-5 h-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 w-72">
                    <SidebarContent onNavigate={() => setSheetOpen(false)} />
                  </SheetContent>
                </Sheet>

                {/* Header app icon + name (visible on mobile - sidebar is hidden) */}
                <div className="flex items-center gap-2 lg:hidden">
                  <AppIcon url={app.icon_url} size={28} />
                  <span className="font-display font-bold text-base" data-testid="header-app-name-mobile">{app.app_name}</span>
                </div>

                {projects.length > 0 && (
                  <Select value={currentId || ""} onValueChange={selectProject}>
                    <SelectTrigger
                      className="w-[220px] sm:w-[260px]"
                      data-testid={DASHBOARD.projectSelector}
                    >
                      <SelectValue placeholder="Pilih proyek" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggle}
                  data-testid={DASHBOARD.themeToggle}
                  aria-label="Toggle theme"
                >
                  {theme === "dark" ? (
                    <SunIcon className="w-4 h-4" />
                  ) : (
                    <Moon className="w-4 h-4" />
                  )}
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2" data-testid={DASHBOARD.userMenu}>
                      <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                        {user?.name?.[0]?.toUpperCase() || "U"}
                      </div>
                      <span className="hidden sm:inline text-sm">{user?.name}</span>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="text-sm">{user?.name}</div>
                      <div className="text-xs text-muted-foreground">{user?.email}</div>
                      <div className="mt-1 inline-block px-1.5 py-0.5 rounded bg-secondary text-xs font-medium uppercase tracking-wider">
                        {user?.role}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="logout-button"
                      onClick={() => {
                        logout();
                        navigate("/login");
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Keluar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
